import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";

const emptyDraft = () => ({
  productType: "",
  quantity: "",
  unit: "KG",
  requirementAmount: "",
});

export default function RequirementEditForm({
  products = [],
  form,
  setForm,
  saving = false,
  onSubmit,
  submitLabel = "Save Changes",
}) {
  const [selectedProductId, setSelectedProductId] = useState("");
  const [useCustomProduct, setUseCustomProduct] = useState(false);
  const [isAmountManual, setIsAmountManual] = useState(false);
  const [localError, setLocalError] = useState("");
  const [editingIndex, setEditingIndex] = useState(-1);

  const draft = form?.draft || emptyDraft();
  const items = Array.isArray(form?.items) ? form.items : [];

  const selectedProduct = useMemo(
    () => products.find((p) => String(p._id) === String(selectedProductId)) || null,
    [products, selectedProductId]
  );
  const selectedProductWeight = useMemo(() => {
    const v = selectedProduct?.weightValue;
    if (v === undefined || v === null || v === "") return "";
    return `${v} ${selectedProduct?.weightUnit || "KG"}`;
  }, [selectedProduct]);
  const selectedProductLabel = useMemo(() => {
    if (!selectedProduct) return "";
    return selectedProductWeight ? `${selectedProduct.name} (${selectedProductWeight})` : selectedProduct.name || "";
  }, [selectedProduct, selectedProductWeight]);

  useEffect(() => {
    const match = products.find(
      (p) =>
        String(p?._id || "") === String(draft?.productId || "") ||
        String(p?.name || "").trim().toLowerCase() ===
        String(draft?.productType || "").trim().toLowerCase()
    );
    if (match) {
      setSelectedProductId(String(match._id));
      setUseCustomProduct(false);
      return;
    }
    setSelectedProductId("");
    setUseCustomProduct(true);
  }, [products, draft?.productId, draft?.productType]);

  useEffect(() => {
    if (!selectedProduct || useCustomProduct) return;
    setForm((prev) => ({
      ...prev,
      draft: {
        ...prev.draft,
        productType: selectedProduct.name || "",
        productId: selectedProduct._id || "",
        packLabel: selectedProductLabel || selectedProduct.name || "",
        unit: selectedProduct.unit || prev.draft?.unit || "KG",
      },
    }));
  }, [selectedProduct, selectedProductLabel, useCustomProduct, setForm]);

  const estimatePricing = useMemo(() => {
    if (!selectedProduct || !Array.isArray(selectedProduct.pricingTiers) || !selectedProduct.pricingTiers.length) {
      return { baseRate: 0, rate: 0, subtotalAmount: 0, discountPercent: 0, discountAmount: 0, gstPercent: 0, gstAmount: 0, totalAmount: 0 };
    }
    const qty = Number(draft?.quantity || 0);
    if (!Number.isFinite(qty) || qty <= 0) {
      return { baseRate: 0, rate: 0, subtotalAmount: 0, discountPercent: 0, discountAmount: 0, gstPercent: Number(selectedProduct?.gstPercent || 0), gstAmount: 0, totalAmount: 0 };
    }
    const tiers = [...selectedProduct.pricingTiers]
      .map((t) => ({
        minQty: Number(t?.minQty ?? 0),
        maxQty: t?.maxQty == null || t?.maxQty === "" ? null : Number(t.maxQty),
        rate: Number(t?.rate || 0),
        discountPercent: Number(t?.discountPercent || 0),
      }))
      .filter((t) => Number.isFinite(t.minQty) && Number.isFinite(t.rate) && t.rate > 0)
      .sort((a, b) => a.minQty - b.minQty);
    const exact = tiers.find((t) => {
      if (t.maxQty == null) return qty >= t.minQty;
      if (!Number.isFinite(t.maxQty)) return false;
      return qty >= t.minQty && qty <= t.maxQty;
    });
    const tier = exact || [...tiers].reverse().find((t) => qty >= t.minQty) || tiers[0] || null;
    const rate = Number(tier?.rate || 0);
    if (!Number.isFinite(rate) || rate <= 0) {
      return { baseRate: 0, rate: 0, subtotalAmount: 0, discountPercent: 0, discountAmount: 0, gstPercent: Number(selectedProduct?.gstPercent || 0), gstAmount: 0, totalAmount: 0 };
    }
    const discountPercent = Number(tier?.discountPercent || 0);
    const discountedRate = Math.round((rate - (rate * discountPercent) / 100 + Number.EPSILON) * 100) / 100;
    const subtotalAmount = Math.round((discountedRate * qty + Number.EPSILON) * 100) / 100;
    const discountAmount = Math.round((((rate * qty) - subtotalAmount) + Number.EPSILON) * 100) / 100;
    const shouldApplyGst =
      String(selectedProduct?.weightUnit || "KG").trim().toUpperCase() === "KG" &&
      Number.isFinite(Number(selectedProduct?.weightValue || 0)) &&
      Number(selectedProduct?.weightValue || 0) >= 1 &&
      Number(selectedProduct?.weightValue || 0) <= 25;
    const gstPercent = shouldApplyGst ? Number(selectedProduct?.gstPercent || 0) : 0;
    const gstAmount = Math.round((((subtotalAmount * gstPercent) / 100) + Number.EPSILON) * 100) / 100;
    return {
      baseRate: rate,
      rate: discountedRate,
      subtotalAmount,
      discountPercent,
      discountAmount,
      gstPercent,
      gstAmount,
      totalAmount: Math.round((subtotalAmount + gstAmount + Number.EPSILON) * 100) / 100,
    };
  }, [selectedProduct, draft?.quantity, draft?.unit]);

  useEffect(() => {
    if (selectedProduct) {
      setForm((prev) => ({
        ...prev,
        draft: {
          ...prev.draft,
          requirementAmount: estimatePricing.totalAmount > 0 ? String(estimatePricing.totalAmount) : "",
        },
      }));
      return;
    }
    if (!isAmountManual && estimatePricing.totalAmount > 0) {
      setForm((prev) => ({
        ...prev,
        draft: {
          ...prev.draft,
          requirementAmount: String(estimatePricing.totalAmount),
        },
      }));
    }
  }, [estimatePricing, isAmountManual, setForm]);

  const handleDraftField = (field, value) => {
    if (field === "requirementAmount") setIsAmountManual(true);
    setForm((prev) => ({
      ...prev,
      draft: {
        ...prev.draft,
        [field]: value,
      },
    }));
  };

  const buildDraftItem = () => {
    const productType = String(draft?.productType || "").trim();
    if (!productType) return { error: "Please select or enter a product." };
    const quantity = Number(draft?.quantity || 0);
    if (!Number.isFinite(quantity) || quantity <= 0) return { error: "Please enter valid quantity." };
    if (selectedProduct && estimatePricing.totalAmount <= 0) {
      return { error: "Selected catalog product price could not be calculated. Please check product pricing tiers." };
    }
    const finalAmount = estimatePricing.totalAmount > 0 ? estimatePricing.totalAmount : Number(draft?.requirementAmount || 0);
    if (!Number.isFinite(finalAmount) || finalAmount <= 0) return { error: "Please enter valid amount." };
    return {
      item: {
        productType,
        productId: selectedProduct?._id || undefined,
        packLabel: selectedProductLabel || productType,
        quantity,
        unit: draft?.unit || "KG",
        rate: estimatePricing.rate || 0,
        subtotalAmount: estimatePricing.subtotalAmount > 0 ? estimatePricing.subtotalAmount : finalAmount,
        discountPercent: estimatePricing.discountPercent > 0 ? estimatePricing.discountPercent : 0,
        discountAmount: estimatePricing.discountAmount > 0 ? estimatePricing.discountAmount : 0,
        gstPercent: estimatePricing.gstPercent > 0 ? estimatePricing.gstPercent : 0,
        gstAmount: estimatePricing.subtotalAmount > 0 ? estimatePricing.gstAmount : 0,
        totalAmount: finalAmount,
        requirementAmount: finalAmount,
      },
    };
  };

  const clearDraft = () => {
    setForm((prev) => ({
      ...prev,
      draft: emptyDraft(),
    }));
    setSelectedProductId("");
    setUseCustomProduct(false);
    setIsAmountManual(false);
    setEditingIndex(-1);
  };

  const addDraftItem = () => {
    const { item, error } = buildDraftItem();
    if (error) {
      setLocalError(error);
      return null;
    }
    setLocalError("");
    let nextItems = [];
    setForm((prev) => {
      const prevItems = Array.isArray(prev.items) ? [...prev.items] : [];
      if (editingIndex >= 0 && editingIndex < prevItems.length) {
        prevItems[editingIndex] = item;
      } else {
        prevItems.push(item);
      }
      nextItems = prevItems;
      return {
        ...prev,
        items: prevItems,
      };
    });
    clearDraft();
    return nextItems;
  };

  const removeItem = (index) => {
    setForm((prev) => ({
      ...prev,
      items: (Array.isArray(prev.items) ? prev.items : []).filter((_, idx) => idx !== index),
    }));
    if (editingIndex === index) {
      clearDraft();
      return;
    }
    if (editingIndex > index) {
      setEditingIndex((prev) => prev - 1);
    }
  };

  const editItem = (item, index) => {
    setLocalError("");
    setEditingIndex(index);
    const matchedProduct = products.find(
      (p) =>
        String(p?._id || "") === String(item?.productId || "") ||
        String(
          item?.packLabel ||
          `${item?.productType || ""}${item?.weightValue ? ` (${item.weightValue} ${item.weightUnit || "KG"})` : ""}`
        ).trim().toLowerCase() ===
        String(
          p?.weightValue !== undefined && p?.weightValue !== null && p?.weightValue !== ""
            ? `${p?.name || ""} (${p.weightValue} ${p.weightUnit || "KG"})`
            : p?.name || ""
        ).trim().toLowerCase() ||
        String(p?.name || "").trim().toLowerCase() ===
        String(item?.productType || "").trim().toLowerCase()
    );
    setSelectedProductId(matchedProduct ? String(matchedProduct._id) : "");
    setUseCustomProduct(!matchedProduct);
    setIsAmountManual(!matchedProduct);
    setForm((prev) => ({
      ...prev,
      draft: {
        productType: item.productType || "",
        productId: item.productId || "",
        packLabel: item.packLabel || "",
        quantity: item.quantity ?? "",
        unit: item.unit || "KG",
        requirementAmount: String(item.requirementAmount || item.totalAmount || ""),
      },
    }));
  };

  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + Number(item?.requirementAmount || item?.totalAmount || 0), 0),
    [items]
  );

  const handleSaveChanges = async () => {
    const hasDraftValues =
      String(draft?.productType || "").trim() ||
      String(draft?.quantity || "").trim() ||
      String(draft?.requirementAmount || "").trim();

    if (hasDraftValues) {
      const { item, error } = buildDraftItem();
      if (error) {
        setLocalError(error);
        return;
      }
      const nextItems = [...items];
      if (editingIndex >= 0 && editingIndex < nextItems.length) {
        nextItems[editingIndex] = item;
      } else {
        nextItems.push(item);
      }
      await onSubmit({
        ...form,
        items: nextItems,
        draft: emptyDraft(),
      });
      return;
    }

    await onSubmit(form);
  };

  return (
    <div className="req-edit-shell">
      {localError ? <Alert variant="danger">{localError}</Alert> : null}
      <div className="row g-3">
        <div className="col-lg-7">
          <div className="border rounded-3 p-3 h-100 bg-white">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <div className="fw-semibold">
                  {editingIndex >= 0 ? "Edit Product Item" : "Product Selection"}
                </div>
                <div className="text-muted small">
                  {editingIndex >= 0
                    ? "Catalog se product change kar sakte ho ya custom product me switch kar sakte ho."
                    : "Multiple products add karke same requirement edit karo."}
                </div>
              </div>
              <Form.Check
                type="switch"
                id="edit-custom-product"
                label="Custom Product"
                checked={useCustomProduct}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setUseCustomProduct(checked);
                  if (checked) setSelectedProductId("");
                }}
              />
            </div>

            <Form.Group className="mb-3">
              <Form.Label>{editingIndex >= 0 ? "Change Catalog Product" : "Select Catalog Product"}</Form.Label>
              <Form.Select
                value={selectedProductId}
                onChange={(e) => {
                  setSelectedProductId(e.target.value);
                  setUseCustomProduct(false);
                }}
              >
                <option value="">Select product</option>
                {products.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name}
                    {p.weightValue !== undefined && p.weightValue !== null && p.weightValue !== ""
                      ? ` (${p.weightValue} ${p.weightUnit || "KG"})`
                      : ""}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {useCustomProduct ? (
              <Form.Group className="mb-3">
                <Form.Label>Custom Product Name</Form.Label>
                <Form.Control
                  value={draft.productType}
                  onChange={(e) => handleDraftField("productType", e.target.value)}
                  placeholder="Type product name"
                />
              </Form.Group>
            ) : null}

            <div className="row g-3">
              <div className="col-md-4">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={draft.quantity}
                  onChange={(e) => handleDraftField("quantity", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Unit</Form.Label>
                <Form.Select
                  value={draft.unit}
                  onChange={(e) => handleDraftField("unit", e.target.value)}
                  disabled={!useCustomProduct && !!selectedProduct}
                >
                  <option value="KG">KG</option>
                  <option value="Ton">Ton</option>
                  <option value="Quintal">Quintal</option>
                  <option value="Litre">Litre</option>
                </Form.Select>
              </div>
              <div className="col-md-4">
                <Form.Label>Requirement Amount</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft.requirementAmount}
                  onChange={(e) => handleDraftField("requirementAmount", e.target.value)}
                  disabled={!useCustomProduct && !!selectedProduct}
                />
              </div>
            </div>

            <div className="mt-2 d-flex justify-content-between align-items-center small text-muted">
              <span>{isAmountManual ? "Manual amount active" : "Auto amount from pricing tiers"}</span>
              {estimatePricing.totalAmount > 0 ? (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none"
                  onClick={() => {
                    setIsAmountManual(false);
                    handleDraftField("requirementAmount", String(estimatePricing.totalAmount));
                  }}
                >
                  Use Auto Price
                </button>
              ) : null}
            </div>
            {!useCustomProduct && selectedProduct ? (
              <div className="small text-muted mt-1">
                Quantity change karte hi amount aur GST automatically recalculate hoga.
              </div>
            ) : null}

            <div className="mt-3 d-flex align-items-center gap-2">
              <Button type="button" onClick={addDraftItem}>
                {editingIndex >= 0 ? "Update Product" : "Add Product"}
              </Button>
              {editingIndex >= 0 ? (
                <Button type="button" variant="outline-secondary" onClick={clearDraft}>
                  Cancel Edit
                </Button>
              ) : null}
              <span className="small text-muted">Added: {items.length}</span>
            </div>

            {items.length ? (
              <div className="mt-3 d-flex flex-column gap-2">
                {items.map((item, idx) => (
                  <div key={`${item.productType}_${idx}`} className="border rounded-2 px-2 py-2 bg-light d-flex justify-content-between gap-2">
                    <div className="small">
                      <strong>{item.productType}</strong>
                      {item.packLabel && item.packLabel !== item.productType ? (
                        <div className="small text-muted">{item.packLabel}</div>
                      ) : null}
                      <div className="text-muted">
                        {item.quantity} {item.unit}
                        {Number(item.rate || 0) > 0 ? ` | Rate ₹ ${Number(item.rate || 0).toLocaleString("en-IN")}/${item.unit || "unit"}` : ""}
                        {Number(item.discountAmount || 0) > 0
                          ? ` | Discount ₹ ${Number(item.discountAmount || 0).toLocaleString("en-IN")}`
                          : ""}
                        {Number(item.gstAmount || 0) > 0
                          ? ` | GST ₹ ${Number(item.gstAmount || 0).toLocaleString("en-IN")}`
                          : ""}
                        {" | "}
                        Final Amount ₹ {Number(item.requirementAmount || item.totalAmount || 0).toLocaleString("en-IN")}
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <Button variant="outline-primary" size="sm" onClick={() => editItem(item, idx)}>
                        Edit
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => removeItem(idx)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            <Form.Group className="mt-3">
              <Form.Label>Location</Form.Label>
              <Form.Control
                value={form.locationText}
                onChange={(e) => setForm((prev) => ({ ...prev, locationText: e.target.value }))}
                placeholder="Full delivery location"
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Packaging, quality, delivery window, special instruction..."
              />
            </Form.Group>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="border rounded-3 p-3 h-100 bg-light">
            <div className="fw-semibold mb-2">Pricing Summary</div>
            {selectedProduct ? (
              <>
                <div className="small text-muted mb-2">
                  {selectedProduct.weightValue !== undefined && selectedProduct.weightValue !== null && selectedProduct.weightValue !== ""
                    ? `Weight: ${selectedProduct.weightValue} ${selectedProduct.weightUnit || "KG"}`
                    : `Unit: ${selectedProduct.unit || "KG"}`}
                </div>
                <div className="small text-muted mb-3">
                  GST: {Number(selectedProduct.gstPercent || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}%
                </div>
                <div className="d-flex flex-column gap-2">
                  {(selectedProduct.pricingTiers || []).map((tier, idx) => {
                    const min = tier.minQty ?? 0;
                    const max = tier.maxQty === null || tier.maxQty === undefined ? null : tier.maxQty;
                    const range = max === null ? `${min}+` : `${min}-${max}`;
                    return (
                      <div key={`${selectedProduct._id}_${idx}`} className="border rounded-2 px-2 py-1 bg-white small">
                        {range} {selectedProduct.unit} | Rs {tier.rate}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="small text-muted">Select product to see tier pricing.</div>
            )}

            <hr />
            <div className="d-flex justify-content-between small mb-2">
              <span>Base Rate</span>
              <strong>
                {Number(estimatePricing.baseRate || 0) > 0
                  ? `₹ ${Number(estimatePricing.baseRate || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/${draft?.unit || "unit"}`
                  : "-"}
              </strong>
            </div>
            <div className="d-flex justify-content-between small mb-2">
              <span>Discounted Rate</span>
              <strong>
                {Number(estimatePricing.rate || 0) > 0
                  ? `₹ ${Number(estimatePricing.rate || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/${draft?.unit || "unit"}`
                  : "-"}
              </strong>
            </div>
            <div className="d-flex justify-content-between small mb-2">
              <span>Current Draft Base</span>
              <strong>₹ {Number(estimatePricing.subtotalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
            </div>
            <div className="d-flex justify-content-between small mb-2">
              <span>Discount</span>
              <strong>
                {estimatePricing.discountPercent > 0
                  ? `${estimatePricing.discountPercent}% (₹ ${Number(estimatePricing.discountAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
                  : "No discount"}
              </strong>
            </div>
            <div className="d-flex justify-content-between small mb-2">
              <span>Current Draft GST</span>
              <strong>
                {estimatePricing.gstPercent > 0
                  ? `${Number(estimatePricing.gstPercent || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}% (₹ ${Number(estimatePricing.gstAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
                  : "No GST"}
              </strong>
            </div>
            <div className="d-flex justify-content-between mb-3">
              <span className="fw-semibold">All Items Total</span>
              <strong>₹ {Number(totalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
            </div>

            <div className="d-grid">
              <Button onClick={handleSaveChanges} disabled={saving || (!items.length && !String(draft?.productType || "").trim())}>
                {saving ? "Saving..." : submitLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
